/**
 * Clients — assign each client to an agent (item 6).
 *
 * Until a client has an `assigned_agent_id`, an agent-role staff member sees
 * nothing for them (Workstream C scoping). This screen is how a brokerage_admin
 * (or super_admin) closes that gap: list the tenant's clients and pick the
 * owning agent per row. Assigning cascades to the client's dossiers server-side.
 *
 * Agents come from the staff roster (GET /admin/staff). super_admin must name a
 * tenant for that roster (Workstream B), so for a super_admin login the agent
 * options can be empty — the client list + Unassign still work, and the note
 * explains it. A brokerage_admin sees their own tenant's agents.
 */

import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminService,
  ApiClientError,
  clientInviteService,
  clientService,
  dossierService,
  staffService,
  type ClientInviteCreated,
  type ClientInvitePending,
  type ClientRow,
  type TenantMembership,
  type UnassignedClientRow,
} from "@re-platform/api-client";
import type { DossierSummary } from "@re-platform/types";
import { Button, Card, Toast } from "@re-platform/ui";

import { api } from "../../lib/api";
import { Avatar, cardList, cardShell, Chevron } from "../../lib/cards";
import { ContactButtons } from "../../lib/contacts";
import {
  actingClientFor,
  brokerageLabel,
  useMergedQuery,
  type Scoped,
} from "../../lib/mergedQuery";
import { useMemberships } from "../../lib/memberships";
import { useRoles } from "../../lib/roles";

function clientName(c: ClientRow): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || c.id.slice(0, 8);
}

interface AgentClient {
  client_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
  template: string | null;
  validated: number;
  total: number;
  /** Which brokerage this client belongs to — badged when the agent is dual. */
  tenant: TenantMembership;
}

/** "Pre-approval · 4/5" — green once every document is validated. */
function ProgressPill({
  template,
  validated,
  total,
}: {
  template: string | null;
  validated: number;
  total: number;
}): React.ReactNode {
  const complete = total > 0 && validated >= total;
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 12.5,
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: 999,
        background: complete
          ? "var(--rp-color-success-faint)"
          : "var(--rp-color-surface-2)",
        color: complete
          ? "var(--rp-color-success)"
          : "var(--rp-color-text-muted)",
      }}
    >
      {template ? `${template} · ` : ""}
      {validated}/{total}
    </span>
  );
}

/**
 * One client row as a card: avatar, name, a progress pill, and either a chevron
 * (whole card links to the client) or a `rightSlot` (e.g. the assign-agent
 * select, in which case only the name links so the control stays clickable).
 */
function ClientCard({
  to,
  name,
  email,
  phone,
  brokerage,
  template,
  validated,
  total,
  rightSlot,
}: {
  to: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  brokerage?: string | null;
  template: string | null;
  validated: number;
  total: number;
  rightSlot?: React.ReactNode;
}): React.ReactNode {
  const detail = (
    <div
      style={{
        marginTop: 4,
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      {total > 0 ? (
        <ProgressPill template={template} validated={validated} total={total} />
      ) : (
        <span style={{ fontSize: 12.5, color: "var(--rp-color-text-muted)" }}>
          No application yet
        </span>
      )}
      {brokerage && (
        <span style={{ fontSize: 12.5, color: "var(--rp-color-text-muted)" }}>
          {brokerage}
        </span>
      )}
      <ContactButtons email={email} phone={phone} />
    </div>
  );

  // rightSlot present → the card carries an interactive control; keep the card
  // a plain div and make only the name a link.
  if (rightSlot) {
    return (
      // The floor on the name column is what makes cardShell's wrap useful
      // here: rightSlot carries a minimum width of its own, so the row has to
      // break before the name column collapses under it.
      <div className="rp-clientcard" style={cardShell}>
        <Avatar name={name} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <Link
            to={to}
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "var(--rp-color-text)",
            }}
          >
            {name}
          </Link>
          {detail}
        </div>
        <div style={{ marginLeft: "auto" }}>{rightSlot}</div>
      </div>
    );
  }

  return (
    <Link
      to={to}
      className="rp-clientcard"
      style={{ ...cardShell, textDecoration: "none", color: "inherit" }}
    >
      <Avatar name={name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 15,
            color: "var(--rp-color-text)",
          }}
        >
          {name}
        </div>
        {detail}
      </div>
      <Chevron />
    </Link>
  );
}

/**
 * One row per distinct client across an agent's dossiers (first dossier wins).
 *
 * Keyed on (tenant, client) rather than client alone: the same person can be a
 * client of BOTH of a dual-licensed agent's brokerages — buying a house through
 * the realty side while financing it through the mortgage side is the whole
 * point of holding two licences — and those are two separate records that must
 * not collapse into one row.
 */
function dedupeScopedClients(
  dossiers: readonly Scoped<DossierSummary>[],
): AgentClient[] {
  const byId = new Map<string, AgentClient>();
  for (const { row: d, tenant } of dossiers) {
    const key = `${tenant.tenant_id}:${d.client_id}`;
    if (!byId.has(key)) {
      byId.set(key, {
        client_id: d.client_id,
        name: d.client_name ?? d.client_email ?? d.client_id.slice(0, 8),
        email: d.client_email ?? null,
        // client_phone isn't on DossierSummary's type yet — the list endpoint
        // ships it, so read it off the row rather than a second fetch.
        phone:
          (d as unknown as { client_phone_e164?: string | null })
            .client_phone_e164 ?? null,
        status: d.status,
        template: d.template_display_name ?? d.template_key ?? null,
        validated: d.slots_validated,
        total: d.slots_total,
        tenant,
      });
    }
  }
  return [...byId.values()];
}

/**
 * Invite panel — agents mint a /join?ref=… link for a new client, optionally
 * emailed straight to them (client.invited → notification-svc). The backend
 * route is agent-only, so brokerage_admin/super_admin get a clear 403 note
 * instead of a broken form result.
 */
function InviteClientPanel() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ClientInviteCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  // A dual-licensed agent must say WHICH brokerage the client is being invited
  // into: the invite binds the client to a tenant, and guessing would put them
  // on the wrong side of the business — with the wrong compliance footer,
  // the wrong forms, and the wrong agreement.
  const memberships = useMemberships();
  const tenants = memberships.data ?? [];
  const isDual = tenants.length > 1;
  const [inviteTenantId, setInviteTenantId] = useState("");
  const chosenTenantId = inviteTenantId || tenants[0]?.tenant_id || "";

  async function createInvite() {
    setBusy(true);
    setError(null);
    setResult(null);
    setCopied(false);
    try {
      const invite = await clientInviteService.create(
        actingClientFor(chosenTenantId, isDual),
        {
          ...(email.trim() ? { client_email: email.trim() } : {}),
          ...(name.trim() ? { client_name: name.trim() } : {}),
        },
      );
      setResult(invite);
      // The new invite belongs in the pending list right below this form.
      void queryClient.invalidateQueries({ queryKey: ["client-invites"] });
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 403) {
        setError(
          "Client invites are created by agents — sign in with an agent account to send one.",
        );
      } else {
        setError(err instanceof Error ? err.message : "Invite failed");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card padding="lg">
      <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>Invite a client</h2>
      <p
        style={{
          margin: "0 0 12px",
          color: "var(--rp-color-text-muted)",
          fontSize: 13.5,
        }}
      >
        Creates a sign-up link tied to you as their agent. Add an email to send
        it to the client directly; leave it blank to copy the link yourself.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <input
          type="email"
          placeholder="client@example.com (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          style={inviteInput(260)}
        />
        <input
          type="text"
          placeholder="Client name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          style={inviteInput(200)}
        />
        {isDual && (
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "var(--rp-color-text-muted)" }}>
              Brokerage
            </span>
            <select
              value={chosenTenantId}
              disabled={busy}
              onChange={(e) => setInviteTenantId(e.target.value)}
              style={{ ...inviteInput(180), width: 180 }}
            >
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>
                  {brokerageLabel(t)} · {t.display_name}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button size="sm" onClick={() => void createInvite()} disabled={busy}>
          {busy ? "Creating…" : "Create invite"}
        </Button>
      </div>
      {error && (
        <p
          style={{
            color: "var(--rp-color-danger)",
            fontSize: 13,
            marginBottom: 0,
          }}
        >
          {error}
        </p>
      )}
      {result && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
            fontSize: 13.5,
          }}
        >
          <code
            style={{
              padding: "6px 10px",
              background: "var(--rp-color-surface-2)",
              borderRadius: 8,
              wordBreak: "break-all",
            }}
          >
            {result.invite_url}
          </code>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(result.invite_url);
              setCopied(true);
            }}
          >
            {copied ? "Copied ✓" : "Copy link"}
          </Button>
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            {result.email_queued
              ? "Email sent to the client."
              : "Share this link with the client."}{" "}
            Expires {result.expires_at.slice(0, 10)}.
          </span>
        </div>
      )}
    </Card>
  );
}

/**
 * Pending client invites — the agent's own un-accepted links.
 *
 * Exists for one conversation: "I never got it." Without this, an agent's only
 * move was to mint a SECOND invite, leaving a trail of live links and no way to
 * see what had already been sent. Resend re-sends the SAME link (so the copy
 * possibly sitting in the client's spam folder keeps working) and extends its
 * expiry; Copy link covers the case where email is the broken part.
 *
 * Spec 023: an invite leaves this list once a client signs up from it. Expired
 * invites sit in a collapsed "Expired (N)" section, where Resend re-opens them
 * for 7 days; Revoke withdraws an invite so its link stops working. Both act
 * through the row's OWN brokerage client, so a dual-licensed agent can manage
 * either brokerage's invites from one list.
 *
 * Agent-only, like the invite form above: the list endpoint is scoped to the
 * calling agent server-side, so admins simply get nothing and the panel hides.
 */
function PendingInvitesPanel() {
  const queryClient = useQueryClient();
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showExpired, setShowExpired] = useState(false);

  const memberships = useMemberships();
  const tenants = memberships.data ?? [];
  const invites = useMergedQuery<ClientInvitePending>(
    tenants,
    ["client-invites"],
    // Admins/super get 403 here (agent-only route) — the merged hook doesn't
    // retry, and an all-error result renders nothing, so the panel just hides.
    (client) => clientInviteService.listPending(client),
    { enabled: tenants.length > 0 },
  );

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ["client-invites"] });
  const onError = (fallback: string) => (err: unknown) =>
    setError(err instanceof Error ? err.message : fallback);

  const resend = useMutation({
    mutationFn: ({ token, tenantId }: { token: string; tenantId: string }) =>
      clientInviteService.resend(
        actingClientFor(tenantId, invites.isDual),
        token,
      ),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onError("Resend failed"),
  });

  const revoke = useMutation({
    mutationFn: ({ token, tenantId }: { token: string; tenantId: string }) =>
      clientInviteService.revoke(
        actingClientFor(tenantId, invites.isDual),
        token,
      ),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onError("Revoke failed"),
  });

  const rows = invites.rows;
  // Nothing to manage, or the caller isn't an agent — stay out of the way.
  if (rows.length === 0) return null;
  const open = rows.filter(({ row }) => !row.expired);
  const expired = rows.filter(({ row }) => row.expired);

  function confirmRevoke(token: string, tenantId: string) {
    if (
      window.confirm(
        "Revoke this invite? Its link will stop working. Anyone who already signed up keeps their account.",
      )
    )
      revoke.mutate({ token, tenantId });
  }

  // A render helper, not a component: a component declared in here would be a
  // new type every render and remount its rows.
  function inviteRow(
    row: ClientInvitePending,
    tenant: TenantMembership,
    testId: string,
  ) {
    return (
      <div
        key={`${tenant.tenant_id}:${row.token}`}
        data-testid={testId}
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          fontSize: 13.5,
          paddingBottom: 8,
          borderBottom: "1px solid var(--rp-color-border)",
        }}
      >
        <strong>
          {row.client_name ?? row.client_email ?? "Link-only invite"}
        </strong>
        {invites.isDual && (
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            {brokerageLabel(tenant)}
          </span>
        )}
        {row.client_name && row.client_email && (
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            {row.client_email}
          </span>
        )}
        <span
          style={{
            color: row.expired
              ? "var(--rp-color-danger)"
              : "var(--rp-color-text-muted)",
          }}
        >
          {row.expired
            ? `Expired ${row.expires_at.slice(0, 10)}`
            : `Expires ${row.expires_at.slice(0, 10)}`}
        </span>
        {row.resent_count > 0 && (
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            Re-sent {row.resent_count}×
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {!row.expired && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(row.invite_url);
                setCopiedToken(row.token);
              }}
            >
              {copiedToken === row.token ? "Copied ✓" : "Copy link"}
            </Button>
          )}
          <Button
            size="sm"
            // No address on file ⇒ nothing to send to; the server 409s and
            // the only useful action really is Copy link.
            disabled={!row.client_email || resend.isPending}
            title={
              row.client_email
                ? `Email the link to ${row.client_email} again`
                : "No email address on file — copy the link instead"
            }
            onClick={() =>
              resend.mutate({ token: row.token, tenantId: tenant.tenant_id })
            }
          >
            {resend.isPending && resend.variables?.token === row.token
              ? "Sending…"
              : row.expired
                ? "Resend (open for 7 more days)"
                : "Resend"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={revoke.isPending}
            title="Stop this link from working"
            onClick={() => confirmRevoke(row.token, tenant.tenant_id)}
          >
            {revoke.isPending && revoke.variables?.token === row.token
              ? "Revoking…"
              : "Revoke"}
          </Button>
        </span>
      </div>
    );
  }

  return (
    <Card padding="lg">
      <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>Pending invites</h2>
      <p
        style={{
          margin: "0 0 12px",
          color: "var(--rp-color-text-muted)",
          fontSize: 13.5,
        }}
      >
        Invites you've sent that nobody has signed up with yet. Resending keeps
        the same link working and gives it a fresh 7 days.
      </p>
      {error && (
        <p
          style={{
            color: "var(--rp-color-danger)",
            fontSize: 13,
            marginTop: 0,
          }}
        >
          {error}
        </p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {open.length === 0 && (
          <p style={{ margin: 0, fontSize: 13.5 }}>No open invites.</p>
        )}
        {open.map(({ row, tenant }) => inviteRow(row, tenant, "invite-row"))}
      </div>
      {expired.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Button
            size="sm"
            variant="ghost"
            aria-expanded={showExpired}
            onClick={() => setShowExpired((v) => !v)}
          >
            {`Expired (${expired.length})`}
          </Button>
          {showExpired && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginTop: 8,
              }}
            >
              {expired.map(({ row, tenant }) =>
                inviteRow(row, tenant, "expired-invite-row"),
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

interface AgentOption {
  agent_id: string | null;
  full_name: string | null;
}

/**
 * Action queue — clients the brokerage's own domain sent us with no agent yet.
 * Oldest first; a row past the 2-hour SLA is flagged overdue (the broker's to
 * work). "overdue" is derived server-side from created_at, not a stored counter.
 */
function ActionQueue({
  clients,
  agents,
  busyId,
  isDual,
  onAssign,
}: {
  clients: readonly Scoped<UnassignedClientRow>[];
  agents: AgentOption[];
  busyId: string | null;
  isDual: boolean;
  // Merged queue → the assignment must name the row's own brokerage.
  onAssign: (
    clientId: string,
    agentId: string | null,
    tenantId: string,
  ) => void;
}) {
  if (clients.length === 0) return null;
  const overdueCount = clients.filter((c) => c.row.overdue).length;
  return (
    <Card padding="lg">
      <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>
        Action needed{" "}
        <span style={{ color: "var(--rp-color-text-muted)", fontWeight: 400 }}>
          · {clients.length} unassigned
          {overdueCount > 0 ? `, ${overdueCount} overdue` : ""}
        </span>
      </h2>
      <p
        style={{
          margin: "0 0 12px",
          color: "var(--rp-color-text-muted)",
          fontSize: 13.5,
        }}
      >
        New clients from your brokerage site, waiting for an agent. Overdue rows
        have passed the 2-hour window and default to the broker.
      </p>
      <div style={cardList}>
        {clients.map(({ row: c, tenant }) => {
          const name =
            [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
            c.email ||
            c.id.slice(0, 8);
          return (
            // A card, not a table row: the select carries a minimum width and
            // a table cell cannot wrap, so on a narrow window "Assign an
            // agent…" ran off the side of the panel with no way to reach it.
            <div
              key={`${tenant.tenant_id}:${c.id}`}
              className="rp-clientcard"
              style={cardShell}
            >
              <Avatar name={name} size={34} />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{name}</div>
                <div
                  style={{
                    color: "var(--rp-color-text-muted)",
                    fontSize: 12.5,
                    marginTop: 2,
                  }}
                >
                  {c.email ?? "—"}
                  {isDual && ` · ${brokerageLabel(tenant)}`}
                </div>
              </div>
              {c.overdue ? (
                <span
                  style={{
                    color: "var(--rp-color-danger)",
                    fontWeight: 600,
                    fontSize: 12.5,
                    whiteSpace: "nowrap",
                  }}
                >
                  Overdue
                </span>
              ) : (
                <span
                  style={{
                    color: "var(--rp-color-text-muted)",
                    fontSize: 12.5,
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.minutes_remaining} min left
                </span>
              )}
              <select
                defaultValue=""
                disabled={busyId === c.id}
                onChange={(e) =>
                  onAssign(c.id, e.target.value || null, tenant.tenant_id)
                }
                style={{
                  padding: "6px 8px",
                  border: "1px solid var(--rp-color-border)",
                  borderRadius: "var(--rp-radius-sm)",
                  font: "inherit",
                  fontSize: 13,
                  background: "var(--rp-color-surface)",
                  minWidth: 180,
                  marginLeft: "auto",
                }}
              >
                <option value="">Assign an agent…</option>
                {agents.map((a) => (
                  <option key={a.agent_id} value={a.agent_id ?? ""}>
                    {a.full_name ?? a.agent_id?.slice(0, 8)}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function inviteInput(width: number): React.CSSProperties {
  return {
    width,
    padding: "8px 10px",
    border: "1px solid var(--rp-color-border)",
    borderRadius: 8,
    font: "inherit",
    background: "var(--rp-color-surface)",
  };
}

export function Clients() {
  const queryClient = useQueryClient();
  const roles = useRoles();
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Agent-role callers get the invite panel only: the client list and the
  // staff roster are admin-gated server-side (403), and their own clients
  // already live on Dashboard/Active. Skip the queries instead of surfacing
  // error cards. (Checkpoint #9 finding.)
  const agentOnly =
    roles.isAgent && !roles.isBrokerageAdmin && !roles.isSuperAdmin;

  // Merged across brokerages for dual-licensed staff (PR 5). A super_admin is
  // NOT merged: they already have an explicit tenant picker, and fanning out
  // across every tenant on the platform is not what "merge my brokerages"
  // means for them.
  const memberships = useMemberships();
  const tenants = roles.isSuperAdmin ? [] : (memberships.data ?? []);

  const clientsQuery = useMergedQuery<ClientRow>(
    tenants,
    ["clients"],
    (client) => clientService.list(client),
    { enabled: !agentOnly && tenants.length > 0 },
  );
  // Action queue: clients with no agent yet, oldest first, with a derived
  // overdue flag against the 2-hour SLA (Phase 13). Admin-gated like the list.
  const queueQuery = useMergedQuery<UnassignedClientRow>(
    tenants,
    ["clients-unassigned-queue"],
    (client) => clientService.unassignedQueue(client),
    { enabled: !agentOnly && tenants.length > 0 },
  );
  // super_admin needs a tenant_id for the roster (Workstream B) → may 422; don't
  // retry, and degrade to an empty agent list (Unassign still works).
  const staffQuery = useQuery({
    queryKey: ["staff"],
    queryFn: () => staffService.listStaff(api),
    retry: false,
    enabled: !agentOnly,
  });

  // Dossier progress for the client cards (template + validated/total). The
  // client list itself carries none, so join it in from the dossier list.
  const clientDossiersQuery = useMergedQuery<DossierSummary>(
    tenants,
    ["clients-dossiers"],
    (client) => dossierService.list(client, { limit: 200 }),
    { enabled: !agentOnly && tenants.length > 0 },
  );
  const dossierByClient = new Map<
    string,
    { template: string | null; validated: number; total: number }
  >();
  for (const { row: d } of clientDossiersQuery.rows) {
    if (!dossierByClient.has(d.client_id)) {
      dossierByClient.set(d.client_id, {
        template: d.template_display_name ?? d.template_key ?? null,
        validated: d.slots_validated,
        total: d.slots_total,
      });
    }
  }

  // Agents can't read /document/clients (admin-only), but they CAN read their
  // own scoped dossiers — derive their assigned-client list from those so the
  // Clients screen isn't just an invite box.
  // The dual-licensed AGENT's own view — the highest-value merge on this
  // screen: one human whose clients are split across two brokerages.
  const agentDossiersQuery = useMergedQuery<DossierSummary>(
    tenants,
    ["agent-dossiers"],
    (client) => dossierService.list(client, { limit: 200 }),
    { enabled: agentOnly && tenants.length > 0 },
  );
  const agentClients = dedupeScopedClients(agentDossiersQuery.rows);

  // Brokerage names for the client rows (a super_admin's list can span
  // brokerages). Best-effort: falls back to the id when tenants aren't loaded.
  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => adminService.listTenants(api),
    retry: false,
    // Only super_admin needs (and can list) tenants for the Brokerage column.
    enabled: !agentOnly && roles.isSuperAdmin,
  });
  const brokerageName = (tenantId: string) => {
    const t = (tenantsQuery.data ?? []).find((x) => x.id === tenantId);
    return t ? t.display_name : tenantId.slice(0, 8);
  };

  const [search, setSearch] = useState("");
  const [assignFilter, setAssignFilter] = useState<
    "all" | "assigned" | "unassigned"
  >("all");

  // The Brokerage column serves two different cases: a super_admin's list can
  // span every tenant (named via the tenants lookup), and a DUAL brokerage_admin's
  // is merged across their two (named from the membership on each row).
  const showBrokerage = roles.isSuperAdmin || clientsQuery.isDual;

  const agents = (staffQuery.data ?? []).filter((m) => m.agent_id != null);
  const filteredClients = clientsQuery.rows.filter(({ row: c, tenant }) => {
    if (assignFilter === "assigned" && !c.assigned_agent_id) return false;
    if (assignFilter === "unassigned" && c.assigned_agent_id) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const brokerage = clientsQuery.isDual
      ? brokerageLabel(tenant)
      : brokerageName(c.tenant_id);
    return (
      clientName(c).toLowerCase().includes(q) ||
      (c.email ?? "").toLowerCase().includes(q) ||
      (showBrokerage && brokerage.toLowerCase().includes(q))
    );
  });

  // The list is merged, so the assignment must be addressed to the tenant the
  // ROW came from — not to whatever the sidebar happens to be showing.
  async function assign(
    clientId: string,
    agentId: string | null,
    tenantId: string,
  ) {
    setBusyId(clientId);
    try {
      await clientService.assignAgent(
        actingClientFor(tenantId, clientsQuery.isDual),
        clientId,
        agentId,
      );
      setToast(agentId ? "Agent assigned." : "Agent cleared.");
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({
        queryKey: ["clients-unassigned-queue"],
      });
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Assignment failed");
    } finally {
      setBusyId(null);
    }
  }

  if (agentOnly) {
    return (
      <>
        <h1 style={{ marginTop: 0 }}>Clients</h1>
        <p style={{ color: "var(--rp-color-text-muted)", marginTop: 0 }}>
          Invite a new client, or review the clients assigned to you below.
        </p>
        <InviteClientPanel />
        <PendingInvitesPanel />

        <h2 style={{ margin: "8px 0 12px", fontSize: 16 }}>Your clients</h2>
        {agentDossiersQuery.isPending && <p style={{ margin: 0 }}>Loading…</p>}
        {!agentDossiersQuery.isPending && agentClients.length === 0 && (
          <p style={{ margin: 0, color: "var(--rp-color-text-muted)" }}>
            No clients assigned to you yet. Invite one above — they'll be linked
            to you as their agent.
          </p>
        )}
        {agentClients.length > 0 && (
          <>
            <input
              type="search"
              placeholder="Search clients…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inviteInput(0), width: "100%", marginBottom: 12 }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {agentClients
                .filter((c) => {
                  const q = search.trim().toLowerCase();
                  return (
                    !q ||
                    c.name.toLowerCase().includes(q) ||
                    (c.email ?? "").toLowerCase().includes(q)
                  );
                })
                .map((c) => (
                  <ClientCard
                    key={`${c.tenant.tenant_id}:${c.client_id}`}
                    to={`/clients/${c.client_id}`}
                    name={c.name}
                    email={c.email}
                    phone={c.phone}
                    brokerage={
                      agentDossiersQuery.isDual
                        ? brokerageLabel(c.tenant)
                        : null
                    }
                    template={c.template}
                    validated={c.validated}
                    total={c.total}
                  />
                ))}
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Clients</h1>
      <p style={{ color: "var(--rp-color-text-muted)", marginTop: 0 }}>
        Assign each client to an agent. Until a client is assigned, their agent
        sees nothing for them.
      </p>

      <ActionQueue
        clients={queueQuery.rows}
        agents={agents}
        busyId={busyId}
        isDual={queueQuery.isDual}
        onAssign={(clientId, agentId, tenantId) =>
          void assign(clientId, agentId, tenantId)
        }
      />

      <InviteClientPanel />
      <PendingInvitesPanel />

      {staffQuery.isError && (
        <Card padding="lg">
          <p style={{ margin: 0 }}>
            Couldn't load the agent list — a super-admin must pick a brokerage
            first (manage assignments from a brokerage-admin login). You can
            still clear assignments below.
          </p>
        </Card>
      )}

      {clientsQuery.isError && (
        <Card padding="lg">
          <p style={{ margin: 0 }}>
            Couldn't load clients: {(clientsQuery.error as Error).message}.
          </p>
        </Card>
      )}

      {clientsQuery.rows.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
            margin: "4px 0 12px",
          }}
        >
          <input
            type="search"
            placeholder={
              showBrokerage
                ? "Search name, email or brokerage…"
                : "Search name or email…"
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inviteInput(280)}
          />
          <select
            value={assignFilter}
            onChange={(e) =>
              setAssignFilter(
                e.target.value as "all" | "assigned" | "unassigned",
              )
            }
            style={{ ...inviteInput(160), width: 160 }}
          >
            <option value="all">All clients</option>
            <option value="assigned">Assigned only</option>
            <option value="unassigned">Unassigned only</option>
          </select>
          <span style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
            {filteredClients.length} of {clientsQuery.rows.length}
          </span>
        </div>
      )}

      {clientsQuery.isPending && <p style={{ margin: 0 }}>Loading…</p>}
      {!clientsQuery.isPending && clientsQuery.rows.length === 0 && (
        <p style={{ margin: 0, color: "var(--rp-color-text-muted)" }}>
          No clients in this brokerage yet.
        </p>
      )}
      {clientsQuery.rows.length > 0 && filteredClients.length === 0 && (
        <p style={{ margin: 0, color: "var(--rp-color-text-muted)" }}>
          No clients match your search or filter.
        </p>
      )}
      {filteredClients.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filteredClients.map(({ row: c, tenant }) => {
            const current = c.assigned_agent_id ?? "";
            // Preserve the current value even if the roster doesn't include it
            // (e.g. super_admin with no agent list loaded).
            const hasCurrentOption =
              current === "" ||
              agents.some((a) => a.agent_id === c.assigned_agent_id);
            const prog = dossierByClient.get(c.id);
            return (
              <ClientCard
                key={`${tenant.tenant_id}:${c.id}`}
                to={`/clients/${c.id}`}
                name={clientName(c)}
                email={c.email}
                phone={
                  (c as unknown as { phone_e164?: string | null }).phone_e164 ??
                  null
                }
                brokerage={
                  showBrokerage
                    ? clientsQuery.isDual
                      ? brokerageLabel(tenant)
                      : brokerageName(c.tenant_id)
                    : null
                }
                template={prog?.template ?? null}
                validated={prog?.validated ?? 0}
                total={prog?.total ?? 0}
                rightSlot={
                  <select
                    value={current}
                    disabled={busyId === c.id}
                    onChange={(e) =>
                      void assign(
                        c.id,
                        e.target.value || null,
                        tenant.tenant_id,
                      )
                    }
                    style={{
                      padding: "6px 8px",
                      border: "1px solid var(--rp-color-border)",
                      borderRadius: "var(--rp-radius-sm)",
                      font: "inherit",
                      fontSize: 13,
                      background: "var(--rp-color-surface)",
                      minWidth: 180,
                    }}
                  >
                    <option value="">Unassigned</option>
                    {!hasCurrentOption && c.assigned_agent_id && (
                      <option value={c.assigned_agent_id}>
                        {c.assigned_agent_id.slice(0, 8)} (current)
                      </option>
                    )}
                    {agents.map((a) => (
                      <option key={a.agent_id} value={a.agent_id ?? ""}>
                        {a.full_name ?? a.agent_id?.slice(0, 8)}
                      </option>
                    ))}
                  </select>
                }
              />
            );
          })}
        </div>
      )}

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}
